import { useMemo, useRef, useState } from 'react';
import type { ListTableGroup, ListTableRow } from 'framework/ui/ui-builder';
import useControlUrlParams from '../useControlUrlParams/useControlUrlParams';
import useDebouncedValue from '../useDebouncedValue';
import { SEARCH_DEBOUNCE_MS } from '../../constants';
import { buildCollectionGroups } from './collectionGroups';
import useCollectionPages from './useCollectionPages';
import type {
  UseAnyCollectionPagerParams,
  UseCollectionPagerParams,
  UseGroupedCollectionPagerParams,
} from './collectionPagerTypes';

export type {
  CollectionGroupedPageResponse,
  CollectionPageResponse,
  CollectionSort,
  FetchCollectionPageParams,
  UseCollectionPagerParams,
  UseGroupedCollectionPagerParams,
} from './collectionPagerTypes';

const useCollectionPagerImplementation = <Row extends ListTableRow, Filters>({
  fetchPage,
  fetchInitialPage,
  viewParamsConfig,
  pageSize,
  searchDebounceMs = SEARCH_DEBOUNCE_MS,
  collectionKey,
  enabled = true,
  grouping,
}: UseAnyCollectionPagerParams<Row, Filters>) => {
  const { params, setFilters, resetFilters, setSort } = useControlUrlParams(viewParamsConfig);
  const [searchValue, setSearchValueState] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue, searchDebounceMs);
  const groupingRef = useRef(grouping);
  groupingRef.current = grouping;
  const isGrouped = Boolean(grouping);

  const { page, ...loading } = useCollectionPages<Row, Filters>({
    fetchPage,
    fetchInitialPage,
    pageSize,
    collectionKey,
    enabled,
    isGrouped,
    filters: params.filters,
    sort: params.sort,
    searchValue: debouncedSearchValue,
    isSearchPending: searchValue !== debouncedSearchValue,
  });

  const groups = useMemo(() => {
    const getSectionId = groupingRef.current?.getSectionId;
    return isGrouped && getSectionId ? buildCollectionGroups(page.rows, page.sections, getSectionId) : undefined;
  }, [isGrouped, page.rows, page.sections]);

  return {
    ...loading,
    rows: page.rows,
    groups,
    totalElements: page.totalElements,
    sort: params.sort,
    setSort,
    filters: params.filters,
    setFilters,
    resetFilters,
    searchValue,
    setSearchValue: (next: string) => setSearchValueState(next),
    appliedSearchValue: page.appliedSearchValue,
  };
};

export type CollectionPager<Row extends ListTableRow, Filters> = Omit<
  ReturnType<typeof useCollectionPagerImplementation<Row, Filters>>,
  'groups'
>;

export type GroupedCollectionPager<Row extends ListTableRow, Filters> = Omit<
  CollectionPager<Row, Filters>,
  | 'rows'
  | 'hasMore'
  | 'isLoadingMore'
  | 'loadMore'
  | 'loadMoreError'
  | 'hasPrevious'
  | 'isLoadingPrevious'
  | 'loadPrevious'
> & { groups: ListTableGroup<Row>[] };

function useCollectionPager<Row extends ListTableRow, Filters>(
  params: UseGroupedCollectionPagerParams<Row, Filters>,
): GroupedCollectionPager<Row, Filters>;
function useCollectionPager<Row extends ListTableRow, Filters>(
  params: UseCollectionPagerParams<Row, Filters>,
): CollectionPager<Row, Filters>;
function useCollectionPager<Row extends ListTableRow, Filters>(
  params: UseAnyCollectionPagerParams<Row, Filters>,
): CollectionPager<Row, Filters> | GroupedCollectionPager<Row, Filters> {
  return useCollectionPagerImplementation(params);
}

export default useCollectionPager;
